// static/table_engine_src/core/selection/keyboard.js

export function installSelectionKeyboard(
  ctx,
  {
    on,
    tbody,
    isActiveTable,
    isLastActiveCtx,
    markActiveTable,
    activeEditorEl,
    focusNavHard,
    focusEditorAtCell,
    rowEls,
    nextNavigableCol,
    firstNavigableCol,
    lastNavigableCol,
    nearestNavigableCol,
    cellHasValue,
    setCellAnchor,
    setCellRangeFromAnchor,
    setSelectedColumns,
    setSelectedRows,
    getSelection,
    exitEditMode,
    editorRC,
    wireHistory,
    wireBulkHistoryHooks,
    scrollCellIntoView,
  } = {},
) {
  const table = ctx.table;
  const cols = Array.isArray(ctx.cols) ? ctx.cols : [];

  let _mouseInstalled = false;
  function wireMouseExcelish() {
    if (_mouseInstalled) return;
    _mouseInstalled = true;
    const isPointerNearBottomRight = (cell, evt, px = 12) => {
      if (!cell || !evt) return false;
      const rect = cell.getBoundingClientRect?.();
      if (!rect) return false;
      return evt.clientX >= rect.right - px && evt.clientY >= rect.bottom - px;
    };

    on(
      tbody,
      "pointerdown",
      (e) => {
        const t = e.target;
        if (!t) return;

        markActiveTable();

        if (activeEditorEl()) return;

        const editor = t.closest?.("input, textarea, select");
        if (!editor) return;
        if (!tbody.contains(editor)) return;

        const td = editor.closest("td[data-col-idx]");
        const tr = editor.closest("tr");
        if (!td || !tr) return;

        if (t.closest?.("button")) return;

        e.preventDefault();
        e.stopPropagation();

        const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
        const c = parseInt(td?.dataset?.colIdx || "-1", 10);

        if (Number.isFinite(r) && r >= 0 && Number.isFinite(c) && c >= 0) {
          setCellAnchor(r, c);
        }

        focusNavHard();
      },
      true,
    );

    on(
      tbody,
      "dblclick",
      (e) => {
        markActiveTable();

        const td = e.target?.closest?.("td[data-col-idx]");
        const tr = e.target?.closest?.("tr");
        if (!td || !tr) return;
        if (!tbody.contains(tr)) return;
        if (td.dataset?.teFillHandleCell && isPointerNearBottomRight(td, e, 12)) return;

        const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
        const c = parseInt(td?.dataset?.colIdx || "-1", 10);
        if (!(Number.isFinite(r) && r >= 0 && Number.isFinite(c) && c >= 0)) return;

        setCellAnchor(r, c);
        focusEditorAtCell(r, c, { selectAll: false });
      },
      true,
    );
  }

  let _keyboardInstalled = false;
  function wireKeyboard() {
    if (_keyboardInstalled) return;
    _keyboardInstalled = true;

    wireMouseExcelish();
    wireHistory();
    wireBulkHistoryHooks();

    const ENTER_MOVES_DOWN = true;
    const findState =
      ctx._teFindState && typeof ctx._teFindState === "object"
        ? ctx._teFindState
        : {
            query: "",
            caseSensitive: false,
            active: false,
            lastTarget: null,
          };
    ctx._teFindState = findState;

    function notifyInfo(msg) {
      try {
        ctx.notify?.info?.(msg);
      } catch {}
      try {
        ctx.announceSelectionA11y?.(msg);
      } catch {}
    }

    function moveAnchorBy(dr, dc, { extend = false, append = false } = {}) {
      const rows = rowEls();
      if (!rows.length) return;

      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: nearestNavigableCol(0, +1) };

      let r = cur.r + dr;
      let c = cur.c;

      if (dc !== 0) c = nextNavigableCol(cur.c, dc);

      r = Math.max(0, Math.min(rows.length - 1, r));
      c = Math.max(0, Math.min(cols.length - 1, c));
      c = nearestNavigableCol(c, dc === 0 ? 0 : dc < 0 ? -1 : +1);

      if (extend) setCellRangeFromAnchor(r, c, { append });
      else setCellAnchor(r, c);

      focusNavHard();
    }

    function moveAnchorTo(r, c, { extend = false, append = false } = {}) {
      const rows = rowEls();
      if (!rows.length) return;

      r = Math.max(0, Math.min(rows.length - 1, r));
      c = Math.max(0, Math.min(cols.length - 1, c));
      c = nearestNavigableCol(c, 0);

      if (extend) setCellRangeFromAnchor(r, c, { append });
      else setCellAnchor(r, c);

      focusNavHard();
    }

    function moveTabWrapped(dir /* -1 or +1 */, { extend = false } = {}) {
      const rows = rowEls();
      if (!rows.length) return;

      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: firstNavigableCol() };

      let r = cur.r;
      let c = cur.c;

      if (dir > 0) {
        const nextC = nextNavigableCol(c, +1);
        if (nextC !== c) {
          c = nextC;
        } else {
          if (r < rows.length - 1) {
            r += 1;
            c = firstNavigableCol();
          }
        }
      } else {
        const prevC = nextNavigableCol(c, -1);
        if (prevC !== c) {
          c = prevC;
        } else {
          if (r > 0) {
            r -= 1;
            c = lastNavigableCol();
          }
        }
      }

      if (extend) setCellRangeFromAnchor(r, c);
      else setCellAnchor(r, c);

      focusNavHard();
    }

    function pageJumpRows(dir /* -1 or +1 */, { extend = false } = {}) {
      const rows = rowEls();
      if (!rows.length) return;

      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: firstNavigableCol() };
      const renderedRows = typeof ctx.getRenderedRowEls === "function" ? ctx.getRenderedRowEls() : null;
      const configured = Number(ctx?.selectionPageJumpRows);
      let jump =
        Number.isFinite(configured) && configured > 0
          ? Math.floor(configured)
          : Array.isArray(renderedRows) && renderedRows.length > 1
            ? renderedRows.length - 1
            : 15;
      jump = Math.max(1, jump);

      moveAnchorTo(cur.r + dir * jump, cur.c, { extend });
    }

    function isSearchableCol(cIdx) {
      const col = cols[cIdx];
      if (!col?.key) return false;
      if (ctx.hiddenCols?.has?.(col.key)) return false;
      const kind = String(col.kind || "").toLowerCase();
      if (kind === "rowhandle" || kind === "actions") return false;
      return true;
    }

    function colLettersToIndex(token) {
      const s = String(token || "").trim().toUpperCase();
      if (!/^[A-Z]+$/.test(s)) return -1;
      let n = 0;
      for (let i = 0; i < s.length; i += 1) {
        n = n * 26 + (s.charCodeAt(i) - 64);
      }
      return n - 1;
    }

    function cellTextForSearch(r, c) {
      const rows = rowEls();
      const tr = rows[r];
      const col = cols[c];
      if (!tr || !col?.key) return "";

      if (col.kind === "input") {
        const el = ctx.getInput?.(tr, col.key) || null;
        if (!el) return "";
        if (String(el.tagName || "").toUpperCase() === "SELECT") {
          const label = String(el.selectedOptions?.[0]?.textContent || "").trim();
          const value = String(el.value || "").trim();
          return label || value;
        }
        const value = String(el.value ?? "").trim();
        const raw = String(el.dataset?.raw ?? "").trim();
        return value || raw;
      }

      if (col.kind === "output") {
        const out = ctx.getOut?.(tr, col.key) || null;
        if (!out) return "";
        const txt = String(out.textContent ?? "").trim();
        const raw = String(out.dataset?.raw ?? "").trim();
        return txt || raw;
      }

      let td = null;
      if (typeof tr.querySelector === "function") {
        td = tr.querySelector(`td[data-col-idx="${String(c)}"]`) || null;
      }
      if (!td && tr.children) td = tr.children[c] || null;
      return String(td?.textContent || "").trim();
    }

    function normalizeFindText(s, caseSensitive) {
      const text = String(s ?? "");
      return caseSensitive ? text : text.toLocaleLowerCase();
    }

    function collectFindMatches(query, caseSensitive) {
      const rows = rowEls();
      const q = normalizeFindText(query, caseSensitive).trim();
      if (!q) return [];
      const out = [];
      for (let r = 0; r < rows.length; r += 1) {
        for (let c = 0; c < cols.length; c += 1) {
          if (!isSearchableCol(c)) continue;
          const txt = normalizeFindText(cellTextForSearch(r, c), caseSensitive);
          if (!txt) continue;
          if (txt.includes(q)) out.push({ r, c });
        }
      }
      return out;
    }

    function isAfterCell(a, b, inclusive = false) {
      if (a.r > b.r) return true;
      if (a.r < b.r) return false;
      if (a.c > b.c) return true;
      if (a.c < b.c) return false;
      return !!inclusive;
    }

    function isBeforeCell(a, b, inclusive = false) {
      if (a.r < b.r) return true;
      if (a.r > b.r) return false;
      if (a.c < b.c) return true;
      if (a.c > b.c) return false;
      return !!inclusive;
    }

    function jumpToFindMatch(dir, { inclusive = false } = {}) {
      const q = String(findState.query || "").trim();
      if (!q) return false;

      const matches = collectFindMatches(q, !!findState.caseSensitive);
      if (!matches.length) {
        notifyInfo(`No matches for "${q}".`);
        return false;
      }

      const rows = rowEls();
      if (!rows.length) return false;
      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: firstNavigableCol() };

      let idx = -1;
      if (dir >= 0) {
        idx = matches.findIndex((m) => isAfterCell(m, cur, inclusive));
        if (idx < 0) idx = 0;
      } else {
        for (let i = matches.length - 1; i >= 0; i -= 1) {
          if (isBeforeCell(matches[i], cur, inclusive)) {
            idx = i;
            break;
          }
        }
        if (idx < 0) idx = matches.length - 1;
      }

      const target = matches[idx];
      if (!target) return false;

      findState.active = true;
      findState.lastTarget = target;
      setCellAnchor(target.r, target.c);
      focusNavHard();
      notifyInfo(`Match ${idx + 1} of ${matches.length}.`);
      return true;
    }

    async function promptFind() {
      const seed = String(findState.query || "");
      if (typeof ctx.promptFind === "function") {
        const out = await ctx.promptFind({
          query: seed,
          caseSensitive: !!findState.caseSensitive,
        });
        if (out == null) return null;
        if (typeof out === "object") {
          return {
            query: String(out.query ?? ""),
            caseSensitive: !!out.caseSensitive,
          };
        }
        return { query: String(out), caseSensitive: !!findState.caseSensitive };
      }
      if (typeof window !== "undefined" && typeof window.prompt === "function") {
        const out = window.prompt("Find text:", seed);
        if (out == null) return null;
        return { query: String(out), caseSensitive: false };
      }
      return null;
    }

    async function promptGoTo(cur) {
      const seed = `R${cur.r + 1}C${cur.c + 1}`;
      if (typeof ctx.promptGoTo === "function") {
        const out = await ctx.promptGoTo({ value: seed });
        if (out == null) return null;
        return String(out);
      }
      if (typeof window !== "undefined" && typeof window.prompt === "function") {
        return window.prompt('Go to (examples: "12", "C", "C12", "R12C3", "12:3"):', seed);
      }
      return null;
    }

    function parseGoTo(text, cur) {
      const raw = String(text || "").trim();
      if (!raw) return null;

      let m = /^r\s*(\d+)\s*c\s*(\d+)$/i.exec(raw);
      if (m) return { r: Number(m[1]) - 1, c: Number(m[2]) - 1 };

      m = /^(\d+)\s*[,:\s]\s*(\d+)$/.exec(raw);
      if (m) return { r: Number(m[1]) - 1, c: Number(m[2]) - 1 };

      m = /^([A-Za-z]+)\s*(\d+)$/.exec(raw);
      if (m) {
        const c = colLettersToIndex(m[1]);
        if (c >= 0) return { r: Number(m[2]) - 1, c };
      }

      m = /^(\d+)$/.exec(raw);
      if (m) return { r: Number(m[1]) - 1, c: cur.c };

      m = /^c\s*(\d+)$/i.exec(raw);
      if (m) return { r: cur.r, c: Number(m[1]) - 1 };

      m = /^col(?:umn)?\s+(\d+)$/i.exec(raw);
      if (m) return { r: cur.r, c: Number(m[1]) - 1 };

      m = /^col(?:umn)?\s+([A-Za-z]+)$/i.exec(raw);
      if (m) {
        const c = colLettersToIndex(m[1]);
        if (c >= 0) return { r: cur.r, c };
      }

      m = /^([A-Za-z]+)$/.exec(raw);
      if (m) {
        const c = colLettersToIndex(m[1]);
        if (c >= 0) return { r: cur.r, c };
      }

      return null;
    }

    function selectColumnAtAnchor({ extend = false, toggle = false } = {}) {
      if (typeof setSelectedColumns !== "function") return;
      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: firstNavigableCol() };
      const col = cols[cur.c];
      const key = col?.key;
      if (!key) return;

      if (extend && Number.isInteger(s.colAnchorIdx)) {
        const a = s.colAnchorIdx;
        const lo = Math.min(a, cur.c);
        const hi = Math.max(a, cur.c);
        const keys = [];
        for (let i = lo; i <= hi; i += 1) {
          const k = cols[i]?.key;
          if (!k) continue;
          keys.push(k);
        }
        setSelectedColumns(keys, { anchorIdx: a });
        return;
      }

      if (toggle) {
        const next = new Set(s.selectedCols || []);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        const anchor = Number.isInteger(s.colAnchorIdx) ? s.colAnchorIdx : cur.c;
        setSelectedColumns([...next], { anchorIdx: anchor });
        return;
      }

      setSelectedColumns([key], { anchorIdx: cur.c });
    }

    function selectRowAtAnchor({ extend = false, toggle = false } = {}) {
      if (typeof setSelectedRows !== "function") return;
      const s = ctx._sel;
      const rows = rowEls();
      if (!rows.length) return;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: firstNavigableCol() };
      const r = Math.max(0, Math.min(rows.length - 1, cur.r));

      if (extend && Number.isInteger(s.rowAnchorIdx)) {
        const a = s.rowAnchorIdx;
        const lo = Math.min(a, r);
        const hi = Math.max(a, r);
        const idxs = [];
        for (let i = lo; i <= hi; i += 1) idxs.push(i);
        setSelectedRows(idxs, { anchorIdx: a });
        return;
      }

      if (toggle) {
        const next = new Set(s.selectedRows || []);
        if (next.has(r)) next.delete(r);
        else next.add(r);
        const anchor = Number.isInteger(s.rowAnchorIdx) ? s.rowAnchorIdx : r;
        setSelectedRows([...next], { anchorIdx: anchor });
        return;
      }

      setSelectedRows([r], { anchorIdx: r });
    }

    function ctrlArrowJump(key, { extend = false } = {}) {
      const rows = rowEls();
      if (!rows.length) return;

      const s = ctx._sel;
      const cur =
        s.cellFocus ||
        s.cellAnchor ||
        { r: 0, c: nearestNavigableCol(0, +1) };

      let r = cur.r;
      let c = cur.c;

      const dr = key === "ArrowUp" ? -1 : key === "ArrowDown" ? +1 : 0;
      const dc = key === "ArrowLeft" ? -1 : key === "ArrowRight" ? +1 : 0;

      const startFilled = cellHasValue(r, c);

      const step = () => {
        if (dr !== 0) {
          const nr = r + dr;
          if (nr < 0 || nr >= rows.length) return false;
          r = nr;
          return true;
        }
        if (dc !== 0) {
          const nc = nextNavigableCol(c, dc);
          if (nc === c) return false;
          c = nc;
          return true;
        }
        return false;
      };

      const moved = step();
      if (!moved) {
        moveAnchorTo(r, c, { extend });
        return;
      }

      const firstStepFilled = cellHasValue(r, c);
      const visited = [{ r, c, filled: firstStepFilled }];
      while (step()) {
        visited.push({ r, c, filled: cellHasValue(r, c) });
      }

      const pickTargetIndex = () => {
        if (!visited.length) return -1;

        // Starting from a filled cell:
        // 1) consume current filled run
        // 2) consume first gap
        // 3) if next filled run exists, land at END of that run (no pit-stop)
        // 4) otherwise land at boundary reached while scanning gap
        if (startFilled) {
          let i = 0;
          while (i < visited.length && visited[i].filled) i += 1;
          if (i >= visited.length) return visited.length - 1;

          let j = i;
          while (j < visited.length && !visited[j].filled) j += 1;
          if (j >= visited.length) return visited.length - 1;

          let k = j;
          while (k < visited.length && visited[k].filled) k += 1;
          return k - 1;
        }

        // Starting from an empty cell:
        // - move across empties to first filled if any
        // - if filled run exists, land at first filled (classic behavior)
        if (!visited[0].filled) {
          let j = 0;
          while (j < visited.length && !visited[j].filled) j += 1;
          if (j >= visited.length) return visited.length - 1;
          return j;
        }

        // Immediate filled from empty start; land at end of that run.
        let k = 0;
        while (k < visited.length && visited[k].filled) k += 1;
        return k - 1;
      };

      const idx = pickTargetIndex();
      if (idx >= 0) {
        r = visited[idx].r;
        c = visited[idx].c;
      }

      moveAnchorTo(r, c, { extend });
    }

    on(
      document,
      "keydown",
      async (e) => {
        const meta = e.metaKey || e.ctrlKey;
        const isMetaOnly = !!e.metaKey && !e.ctrlKey;
        const isCtrlOnly = !!e.ctrlKey && !e.metaKey;
        const isUndo = meta && !e.shiftKey && (e.key === "z" || e.key === "Z");
        const isRedo =
          (meta && e.shiftKey && (e.key === "z" || e.key === "Z")) ||
          (meta && (e.key === "y" || e.key === "Y"));

        const allowGlobalUndoRedo = !!isLastActiveCtx?.() && (isUndo || isRedo);
        if (!isActiveTable?.() && !allowGlobalUndoRedo) return;

        const ae = document.activeElement;
        if (ae && !table.contains(ae)) {
          const isFormEl = ae.matches?.("input, textarea, select");
          const isCE = !!ae.isContentEditable;
          if (isFormEl || isCE) return;
        }

        if (typeof ctx.isMenuOpen === "function" && ctx.isMenuOpen()) return;

        if (isUndo || isRedo) {
          e.preventDefault();
          e.stopPropagation();

          const editor = activeEditorEl();
          if (editor) {
            const rc = editorRC(editor);
            try {
              editor.blur();
            } catch {}
            if (rc) {
              try {
                setCellAnchor(rc.r, rc.c);
              } catch {}
            }
          }

          if (isUndo) {
            if (typeof ctx.cmdUndo === "function") await ctx.cmdUndo();
            return;
          }
          if (isRedo) {
            if (typeof ctx.cmdRedo === "function") await ctx.cmdRedo();
            return;
          }
        }

        if (meta && (e.key === "c" || e.key === "C")) {
          e.preventDefault();
          e.stopPropagation();
          if (ctx.cmdCopy) await ctx.cmdCopy();
          else await ctx.copyToClipboard?.();
          return;
        }
        if (meta && (e.key === "v" || e.key === "V")) {
          e.preventDefault();
          e.stopPropagation();
          if (ctx.cmdPaste) await ctx.cmdPaste();
          else await ctx.pasteFromClipboard?.();
          return;
        }
        if (meta && !e.shiftKey && !e.altKey && (e.key === "d" || e.key === "D")) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof ctx.fillDownFromSelection === "function") await ctx.fillDownFromSelection();
          return;
        }

        if (meta && !e.shiftKey && !e.altKey && (e.key === "f" || e.key === "F")) {
          e.preventDefault();
          e.stopPropagation();

          const editor = activeEditorEl();
          if (editor) {
            const rc = editorRC(editor);
            try {
              editor.blur();
            } catch {}
            if (rc) setCellAnchor(rc.r, rc.c);
          }

          const res = await promptFind();
          if (!res) return;
          findState.query = String(res.query || "");
          findState.caseSensitive = !!res.caseSensitive;
          findState.active = !!String(findState.query).trim();
          if (!findState.active) {
            findState.lastTarget = null;
            notifyInfo("Find cleared.");
            return;
          }
          jumpToFindMatch(+1, { inclusive: true });
          return;
        }

        if (meta && !e.shiftKey && !e.altKey && (e.key === "g" || e.key === "G")) {
          e.preventDefault();
          e.stopPropagation();

          const editor = activeEditorEl();
          if (editor) {
            const rc = editorRC(editor);
            try {
              editor.blur();
            } catch {}
            if (rc) setCellAnchor(rc.r, rc.c);
          }

          const s = ctx._sel;
          const cur =
            s.cellFocus ||
            s.cellAnchor ||
            { r: 0, c: firstNavigableCol() };
          const out = await promptGoTo(cur);
          if (out == null) return;
          const target = parseGoTo(out, cur);
          if (!target) {
            notifyInfo("Invalid Go To target.");
            return;
          }
          moveAnchorTo(target.r, target.c);
          return;
        }

        const editor = activeEditorEl();

        if (editor) {
          const leaveEditorAndRestoreAnchor = () => {
            const rc = editorRC(editor) || ctx._sel?.cellFocus || ctx._sel?.cellAnchor || { r: 0, c: firstNavigableCol() };
            try {
              editor.blur();
            } catch {}
            setCellAnchor(rc.r, rc.c);
            return rc;
          };

          if (
            (e.ctrlKey || e.metaKey) &&
            (e.key === "ArrowLeft" ||
              e.key === "ArrowRight" ||
              e.key === "ArrowUp" ||
              e.key === "ArrowDown")
          ) {
            e.preventDefault();
            e.stopPropagation();
            leaveEditorAndRestoreAnchor();
            ctrlArrowJump(e.key, { extend: e.shiftKey });
            return;
          }

          if ((e.ctrlKey || e.metaKey) && (e.key === "Home" || e.key === "End")) {
            e.preventDefault();
            e.stopPropagation();
            leaveEditorAndRestoreAnchor();
            const rows = rowEls();
            if (!rows.length) return;
            if (isMetaOnly) {
              ctrlArrowJump(e.key === "Home" ? "ArrowLeft" : "ArrowRight", { extend: e.shiftKey });
            } else if (isCtrlOnly || (e.ctrlKey && e.metaKey)) {
              if (e.key === "Home") moveAnchorTo(0, 0, { extend: e.shiftKey });
              else moveAnchorTo(rows.length - 1, cols.length - 1, { extend: e.shiftKey });
            } else if (e.key === "Home") {
              const s = ctx._sel;
              const cur = s.cellFocus || s.cellAnchor || { r: 0, c: 0 };
              moveAnchorTo(cur.r, 0, { extend: e.shiftKey });
            } else {
              const s = ctx._sel;
              const cur = s.cellFocus || s.cellAnchor || { r: 0, c: 0 };
              moveAnchorTo(cur.r, cols.length - 1, { extend: e.shiftKey });
            }
            return;
          }

          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            exitEditMode({ keepAnchor: true });
            return;
          }

          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            exitEditMode({ keepAnchor: true });
            if (ENTER_MOVES_DOWN) moveAnchorBy(1, 0, { extend: false });
            return;
          }

          if (e.key === "Tab") {
            e.preventDefault();
            e.stopPropagation();

            const rc = leaveEditorAndRestoreAnchor();

            const dir = e.shiftKey ? -1 : +1;
            const rows = rowEls();
            let r = rc.r;
            let c = rc.c;

            if (dir > 0) {
              const nextC = nextNavigableCol(c, +1);
              if (nextC !== c) c = nextC;
              else if (r < rows.length - 1) {
                r += 1;
                c = firstNavigableCol();
              }
            } else {
              const prevC = nextNavigableCol(c, -1);
              if (prevC !== c) c = prevC;
              else if (r > 0) {
                r -= 1;
                c = lastNavigableCol();
              }
            }

            setCellAnchor(r, c);
            focusEditorAtCell(r, c, { selectAll: true });
            scrollCellIntoView(r, c);
            return;
          }

          if (
            e.altKey &&
            (e.key === "ArrowLeft" ||
              e.key === "ArrowRight" ||
              e.key === "ArrowUp" ||
              e.key === "ArrowDown")
          ) {
            e.preventDefault();
            e.stopPropagation();
            exitEditMode({ keepAnchor: true });
            if (e.key === "ArrowLeft") moveAnchorBy(0, -1, { extend: e.shiftKey });
            if (e.key === "ArrowRight") moveAnchorBy(0, +1, { extend: e.shiftKey });
            if (e.key === "ArrowUp") moveAnchorBy(-1, 0, { extend: e.shiftKey });
            if (e.key === "ArrowDown") moveAnchorBy(+1, 0, { extend: e.shiftKey });
            return;
          }

          return;
        }

        if (e.key === "F2") {
          const s = ctx._sel;
          const cur = s.cellFocus || s.cellAnchor || { r: 0, c: nearestNavigableCol(0, +1) };
          e.preventDefault();
          e.stopPropagation();
          focusEditorAtCell(cur.r, cur.c, { selectAll: true });
          return;
        }

        if (meta && !e.shiftKey && !e.altKey && (e.key === "d" || e.key === "D")) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof ctx.fillDownFromSelection === "function") await ctx.fillDownFromSelection();
          return;
        }

        if (e.key === "Enter") {
          if (!meta && !e.altKey && findState.active && String(findState.query || "").trim()) {
            e.preventDefault();
            e.stopPropagation();
            jumpToFindMatch(e.shiftKey ? -1 : +1);
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          moveAnchorBy(1, 0, { extend: false });
          return;
        }

        if (e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
          moveTabWrapped(e.shiftKey ? -1 : +1, { extend: false });
          return;
        }

        const isSpaceKey = e.key === " " || e.key === "Spacebar" || e.code === "Space";
        if (isSpaceKey && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();
          selectColumnAtAnchor({
            extend: e.shiftKey && Number.isInteger(ctx?._sel?.colAnchorIdx),
            toggle: e.altKey,
          });
          return;
        }
        if (isSpaceKey && e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          selectRowAtAnchor({
            extend: Number.isInteger(ctx?._sel?.rowAnchorIdx),
            toggle: e.altKey,
          });
          return;
        }
        if (isSpaceKey && e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          selectRowAtAnchor({ toggle: true, extend: false });
          return;
        }

        if (e.key === "Home") {
          e.preventDefault();
          e.stopPropagation();
          if (isMetaOnly) {
            ctrlArrowJump("ArrowLeft", { extend: e.shiftKey });
          } else if (isCtrlOnly || (e.ctrlKey && e.metaKey)) {
            moveAnchorTo(0, 0, { extend: e.shiftKey });
          } else {
            const s = ctx._sel;
            const cur = s.cellFocus || s.cellAnchor || { r: 0, c: 0 };
            moveAnchorTo(cur.r, 0, { extend: e.shiftKey });
          }
          return;
        }

        if (e.key === "End") {
          e.preventDefault();
          e.stopPropagation();
          const rows = rowEls();
          if (!rows.length) return;

          if (isMetaOnly) {
            ctrlArrowJump("ArrowRight", { extend: e.shiftKey });
          } else if (isCtrlOnly || (e.ctrlKey && e.metaKey)) {
            moveAnchorTo(rows.length - 1, cols.length - 1, { extend: e.shiftKey });
          } else {
            const s = ctx._sel;
            const cur = s.cellFocus || s.cellAnchor || { r: 0, c: 0 };
            moveAnchorTo(cur.r, cols.length - 1, { extend: e.shiftKey });
          }
          return;
        }

        if (e.key === "PageUp") {
          e.preventDefault();
          e.stopPropagation();
          pageJumpRows(-1, { extend: e.shiftKey });
          return;
        }

        if (e.key === "PageDown") {
          e.preventDefault();
          e.stopPropagation();
          pageJumpRows(+1, { extend: e.shiftKey });
          return;
        }

        if (e.key === "Backspace" || e.key === "Delete") {
          const sel = getSelection();
          if (!sel) return;

          e.preventDefault();
          e.stopPropagation();

          if (ctx.cmdClearSelection) await ctx.cmdClearSelection();
          else {
            if (ctx._sel.selectedCols?.size) ctx.clearSelectedColumns?.();
            else if (ctx._sel.selectedRows?.size) ctx.clearSelectedRowsInputs?.();
            else ctx.clearRange?.(sel);
          }
          return;
        }

        if (
          (e.key === "ArrowLeft" ||
            e.key === "ArrowRight" ||
            e.key === "ArrowUp" ||
            e.key === "ArrowDown") &&
          (e.ctrlKey || e.metaKey)
        ) {
          e.preventDefault();
          e.stopPropagation();
          ctrlArrowJump(e.key, { extend: e.shiftKey });
          return;
        }

        if (
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "ArrowUp" ||
          e.key === "ArrowDown"
        ) {
          e.preventDefault();
          e.stopPropagation();

          if (e.key === "ArrowLeft") moveAnchorBy(0, -1, { extend: e.shiftKey });
          if (e.key === "ArrowRight") moveAnchorBy(0, +1, { extend: e.shiftKey });
          if (e.key === "ArrowUp") moveAnchorBy(-1, 0, { extend: e.shiftKey });
          if (e.key === "ArrowDown") moveAnchorBy(+1, 0, { extend: e.shiftKey });
        }
      },
      true,
    );
  }

  return {
    wireMouseExcelish,
    wireKeyboard,
  };
}
